// @flow

import React from 'react';
import { Translate } from 'react-redux-i18n';
import { splitThousand } from '../../../../utils/formatUtils';
import Tooltip from '../../../../components/Tooltip';

type Props = {
  kpiParam: any,
  onChange: (event: EventTarget) => {},
  pattern: string,
  isFocus: boolean,
  convertValueToCurrency: (number | string) => string,
  undoConvertValueToCurrency: (number | string) => number,
  currencyUnit: string,
  locked: boolean,
  setLocked: () => void,
  showTooltipWarning: boolean,
}

function RoiKpiParamField({
  kpiParam, onChange, pattern, isFocus, locked, setLocked,
  convertValueToCurrency, undoConvertValueToCurrency, currencyUnit, showTooltipWarning,
}: Props) {
  const formatValue = (newValue: number | string = ''): string => {
    const currentVal = newValue.toString()
      .replace(/[a-zA-Z\s]+/g, '');
    const intPortion = splitThousand(currentVal.split('.')[0])
      .replace('N/A', '');
    const decimalPresence = currentVal.includes('.') ? '.' : '';
    const decimals = currentVal.split('.').slice(1).join('');
    return `${intPortion}${decimalPresence}${decimals}`;
  };

  const formatByCurrency = (newValue: number | string): string =>
    formatValue(convertValueToCurrency(newValue));

  const reformatByUnit = (newValue: number | string): number | string =>
    formatValue(newValue.replace(/[\s.]+/g, ''));

  const isASpaceBeforePointer = (newValue: string, cursorPos: number): boolean =>
    (cursorPos === 0 ? false : newValue.substring(cursorPos - 1, cursorPos) === ' ');

  const handleChange = (e: EventHandler) => {
    if (!locked) {
      setLocked();
    }
    let { value } = e.target;
    const cursorPosToEnd = value.length - e.target.selectionStart;
    value = reformatByUnit(value);
    const unformattedValue = undoConvertValueToCurrency(value);
    onChange(unformattedValue);
    e.target.value = value;
    const newPosition = Math.max(value.length - cursorPosToEnd, 0);
    if (isASpaceBeforePointer(e.target.value, newPosition)) {
      e.target.setSelectionRange(newPosition - 1, newPosition - 1);
    } else {
      e.target.setSelectionRange(newPosition, newPosition);
    }
  };

  return (
    <div className={`roi-kpi${isFocus ? ' roi-kpi--focus' : ''}`}>
      <div className="roi-kpi-param-field">
        <i
          role="button"
          tabIndex={0}
          className={`locker ${locked ? 'fas fa-lock' : 'fas fa-unlock'}`}
          onClick={setLocked}
        />
        <input
          type="text"
          className="roi-kpi-value roi-input"
          value={formatByCurrency(kpiParam)}
          onChange={(e: EventHandler) => handleChange(e)}
          pattern={pattern}
        />
        <span className="roi-param-unit">{currencyUnit}</span>
        {
          showTooltipWarning && !locked ?
            (
              <Tooltip icon={<i className="fas fa-exclamation-triangle roi-kpi--warning" />}>
                <Translate value="roi.tooltip.warning" />
              </Tooltip>
            ) : (
              <Tooltip icon={<i className="fa fa-info-circle" />}>
                <Translate value="roi.tooltip.info" />
              </Tooltip>
            )
        }
      </div>
    </div>
  );
}

export default RoiKpiParamField;
